import { type ColumnType, type Selectable } from 'kysely'
import { z } from 'zod'

import { legacySiteId, type LegacySiteId } from './LegacySite.js'
import {
  lengthUnitType,
  type default as LengthUnitType,
} from './LengthUnitType.js'
import { organizationId, type OrganizationId } from './Organization.js'
import { projectId, type ProjectId } from './Project.js'
import { sitePageTypeId, type SitePageTypeId } from './SitePageType.js'
import {
  siteStatusType,
  type default as SiteStatusType,
} from './SiteStatusType.js'

/** Represents the view public.site_table_view_ */
export default interface SiteTableViewTable {
  /** Database type: pg_catalog.int4 */
  id: ColumnType<LegacySiteId, never, never>

  /** Database type: pg_catalog.text */
  siteName: ColumnType<string, never, never>

  /** Database type: pg_catalog.bool */
  visible: ColumnType<boolean | null, never, never>

  /** Database type: public.site_status_type_ */
  status: ColumnType<SiteStatusType | null, never, never>

  /** Database type: pg_catalog.int4 */
  sortOrder: ColumnType<number | null, never, never>

  /** Database type: pg_catalog.text */
  internalNotes: ColumnType<string | null, never, never>

  /** Database type: pg_catalog.numeric */
  sonicHeight: ColumnType<number | null, never, never>

  /** Database type: pg_catalog.int4 */
  readingsVersion: ColumnType<number | null, never, never>

  /** Database type: public.length_unit_type_ */
  lengthUnits: ColumnType<LengthUnitType, never, never>

  /** Database type: pg_catalog.numeric */
  geoLat: ColumnType<number | null, never, never>

  /** Database type: pg_catalog.numeric */
  geoLon: ColumnType<number | null, never, never>

  /** Database type: pg_catalog.int4 */
  projectId: ColumnType<ProjectId, never, never>

  /** Database type: pg_catalog.text */
  projectName: ColumnType<string, never, never>

  /** Database type: pg_catalog.int4 */
  organizationId: ColumnType<OrganizationId, never, never>

  /** Database type: pg_catalog.text */
  organizationName: ColumnType<string, never, never>

  /** Database type: pg_catalog.jsonb */
  loggerHistory: ColumnType<unknown, never, never>

  /** Database type: pg_catalog.jsonb */
  cableHistory: ColumnType<unknown, never, never>

  /** Database type: pg_catalog.text */
  pageType: ColumnType<SitePageTypeId, never, never>
}

export type SiteTableView = Selectable<SiteTableViewTable>

export const siteTableView = z.object({
  id: legacySiteId,
  siteName: z.string(),
  visible: z.boolean().nullable().optional(),
  status: siteStatusType.nullable().optional(),
  sortOrder: z.number().nullable().optional(),
  internalNotes: z.string().nullable().optional(),
  sonicHeight: z.number().nullable().optional(),
  readingsVersion: z.number().nullable().optional(),
  lengthUnits: lengthUnitType,
  geoLat: z.number().nullable().optional(),
  geoLon: z.number().nullable().optional(),
  projectId: projectId,
  projectName: z.string(),
  organizationId: organizationId,
  organizationName: z.string(),
  loggerHistory: z.unknown(),
  cableHistory: z.unknown(),
  pageType: sitePageTypeId,
}) as unknown as z.Schema<SiteTableView>
